import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import { type ErrorComponentProps, Link, useRouter } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import s from './ui.module.css';

/** A placeholder block for a part of a page that is still loading. */
export function Loading({ height = 160 }: { height?: number }) {
  const { t } = useTranslation();
  return (
    <div className={s.skeleton} style={{ height }} role="status">
      <span className="visually-hidden">{t('common.loading')}</span>
    </div>
  );
}

/** Whole-page skeleton: hero, two cards side by side, one tall card. */
export function PageLoading() {
  const { t } = useTranslation();
  return (
    <div className={s.pageSkeleton} role="status" aria-busy="true" aria-label={t('common.loading')}>
      <span className={s.skeletonHero} />
      <div className={s.skeletonRow}>
        <span />
        <span />
      </div>
      <span className={s.skeletonFill} />
    </div>
  );
}

export function ErrorPanel({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation();
  if (error instanceof ApiError && error.code === 'FORBIDDEN') return <Forbidden />;
  if (error instanceof ApiError && error.code === 'NOT_FOUND') return <NotFound />;
  return (
    <div className={s.errorPanel} role="alert">
      <h1>{t('common.errorTitle')}</h1>
      <p>{t('common.errorBody')}</p>
      {error instanceof ApiError && error.requestId ? (
        <code>{t('common.requestId', { id: error.requestId })}</code>
      ) : null}
      {onRetry ? (
        <button type="button" className={s.stateButton} onClick={onRetry}>
          {t('common.retry')}
        </button>
      ) : null}
    </div>
  );
}

/**
 * A route's errorComponent. Retry has to clear the failed queries too: a suspense query keeps its error
 * and throws it again on the next render without fetching. Then the route data reloads and the page
 * renders again.
 */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const queries = useQueryErrorResetBoundary();
  return (
    <ErrorPanel
      error={error}
      onRetry={() => {
        queries.reset();
        reset();
        void router.invalidate();
      }}
    />
  );
}

// Forbidden and NotFound replace a whole page, so their title is the page's h1 (route focus lands on it).
export function Forbidden() {
  const { t } = useTranslation();
  return (
    <div className={s.codeState}>
      <span className={s.stateCode}>403</span>
      <h1>{t('common.forbiddenTitle')}</h1>
      <Link to="/" className={s.stateButton}>
        {t('common.home')}
      </Link>
    </div>
  );
}

export function NotFound() {
  const { t } = useTranslation();
  return (
    <div className={s.codeState}>
      <span className={s.stateCode}>404</span>
      <h1>{t('common.notFoundTitle')}</h1>
      <Link to="/" className={s.stateButton}>
        {t('common.home')}
      </Link>
    </div>
  );
}

/** Empty page or section: a card with the message and an optional action. */
export function EmptyState({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className={s.empty}>
      <p>{children}</p>
      {action}
    </div>
  );
}

export function Card({
  title,
  aside,
  children,
  id,
}: {
  title?: string;
  aside?: ReactNode;
  children: ReactNode;
  id?: string;
}) {
  return (
    <section className={s.card} aria-labelledby={title && id ? id : undefined}>
      {title ? (
        <div className={s.cardHead}>
          <h2 id={id}>{title}</h2>
          {aside}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export const fieldStyles = { field: s.field, input: s.input, hint: s.hint, error: s.fieldError };
